"use client"

import { Minus, Plus, Question } from '@/lib/icons'
import Link from 'next/link'
import { useState } from 'react'

const questions = [
    {
        question: 'Are you able to work smoothly with clients in other time zones?',
        answer: 'Absolutely. I’ve worked with clients from all over the globe, and coordinating around time zone differences is something I handle routinely.'
    },
    {
        question: 'Which technologies excite you the most?',
        answer: 'React, Next.js, and Node.js are my favorites by far. I also love building engaging games with Unity and Phaser.js, and I work confidently with databases such as MongoDB, Firebase, PostgreSQL, MySQL, and MSSQL.'
    },
    {
        question: 'How do you make sure the work you deliver is high quality?',
        answer: 'Quality comes first for me. I follow industry best practices, keep my code clean and maintainable, and test rigorously so that every project holds up.'
    },
    {
        question: 'What is your attitude toward client feedback?',
        answer: 'I value it enormously. Every note is a chance to refine the work and grow, so constructive criticism is always welcome, and I stay committed to delivering exceptional results.'
    },
    {
        question: 'Is support available once a project wraps up?',
        answer: 'Yes. I would rather build lasting partnerships than one-off deliveries, so I remain available for maintenance, support, and updates long after the project is finished.'
    },
    {
        question: 'How much availability do you usually have for projects?',
        answer: 'I work full-time and shape my schedule around whatever the client needs, which keeps collaboration running smoothly.'
    },
    {
        question: 'How do you keep tight deadlines from slipping?',
        answer: 'Tight deadlines are a challenge I am well prepared for. Careful time management lets me prioritize the right tasks and ship on schedule without letting quality slide.'
    },
    {
        question: 'Have you handled projects with large-scale complexity?',
        answer: 'Yes. I’ve contributed to several large-scale projects, working through intricate requirements alongside diverse teams to reach outstanding outcomes.'
    }
]

const FAQs = ({ lessMarginTop = false }: { lessMarginTop?: boolean }) => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null)

    const toggleQuestion = (index: number) => {
        if (activeIndex === index) {
            setActiveIndex(null)
        } else {
            setActiveIndex(index)
        }
    }

    return (
        <section className={`w-full mb-6 ${lessMarginTop ? "mt-8 sm:mt-10 md:mt-16" : "mt-16 sm:mt-20 md:mt-32"} px-2 sm:px-4 xl:px-0 max-w-5xl mx-auto`}>
            <h2 className="text-center font-bold capitalize text-2xl sm:text-3xl md:text-4xl">
                Frequently Asked Questions
            </h2>
            <p className="text-sm sm:text-base mt-2 mx-auto text-light/60 text-center max-w-3xl">
                Don&apos;t see your question here? No problem — <Link href="https://tajammalmaqbool-faqbot.zapier.app/" rel="noopener noreferrer" target='_blank' className="text-accent font-semibold hover:underline">chat with my FAQ Bot</Link> for an instant answer, or just reach out directly. I&apos;m happy to help!
            </p>
            <div className='flex gap-4 mt-4'>
                <div className='w-full max-w-80 bg-dark3 border-2 border-light/5 p-4 pb-8 hidden h-fit rounded-2xl md:flex md:items-center md:justify-center md:flex-col'>
                    <div
                        className='bg-light/10 p-2 mt-6 rounded-full flex-center'
                    >
                        <Question className="text-accent text-3xl" />
                    </div>
                    <h3 className="font-bold text-2xl mt-2">
                        Ask a Question
                    </h3>
                    <p className="text-light/60 text-center text-xs md:text-sm">
                        Can&apos;t find what you&apos;re looking for? Send your question over and I&apos;ll get back to you
                    </p>
                    <Link href="/contact"
                        className="max-w-fit px-8 button-accent mt-6"
                    >
                        Ask a Question
                    </Link>
                </div>

                <div className='flex flex-col gap-2 md:gap-3'>
                    {questions.map((faq, index) => {
                        const isOpen = index === activeIndex

                        return (
                            <div
                                key={index}
                                onClick={() => toggleQuestion(index)}
                                className={`transition-all duration-700 cursor-pointer ease-in-out ${isOpen ? "brightness-110" : ""} hover:brightness-110 w-full bg-dark3 hover:bg-dark2 border-2 border-light/5 rounded-xl p-4`}>
                                <div
                                    className="flex justify-between items-start">
                                    <h3 className="text-light font-normal text-base sm:text-lg">
                                        {faq.question}
                                    </h3>
                                    <div
                                        className={`flex-center text-accent/80 text-xl ${isOpen ? "rotate-0" : "rotate-90"} mt-0.5 transition-all ease-in-out duration-300`}
                                    >
                                        {isOpen ? <Minus /> : <Plus />}
                                    </div>
                                </div>

                                <div
                                    className="overflow-hidden transition-all ease-out duration-300"
                                    style={{
                                        visibility: isOpen ? 'visible' : 'hidden',
                                        height: isOpen ? 'auto' : '0px',
                                    }}
                                >
                                    <p className="text-light/60 mt-2 text-sm sm:text-base">
                                        {faq.answer}
                                    </p>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default FAQs